<template>
  <v-container 
  v-if="isDataReady"
  fluid>
    <attachment-dialog
    v-if="attachmentDialog.model"
    v-model="attachmentDialog.model"
    :attachments="attachmentDialog.attachments"
    :preview="attachmentDialog.preview"/>
    <saveable-attachment-dialog
    v-if="serviceAttachmentDialog.model"
    v-model="serviceAttachmentDialog.model"
    :attachments.sync="remoteSupportProp.attachmentsByService"
    :preview="serviceAttachmentDialog.preview"
    :on-save="tryToSaveAttachmentsByService"/>

    <section-title-with-request-id
    :request-id="remoteSupportProp.requestId"
    title="pages.remoteSupport.reviewData"/>

    <v-row
    justify="center">
      <v-col
      cols="6">
        <v-row>
          <v-col>
            <p class="process-repair-tab-section-subtitle">
              {{$t('textFields.attachments')}}
            </p>
            <v-row>
              <v-col
              class="pb-6"
              cols="6">
                <div class="d-flex justify-center">
                  <attachments-dialog-trigger
                  @click="openAttachmentsDialog(attachmentsByClient, { preview: true })"
                  :attachments="attachmentsByClient"
                  disable-on-empty/>
                </div>
                <p class="text-center process-repair-tab-section-subtitle font-italic">
                  {{$t("btns.byClient")}}
                </p>
              </v-col>
              <v-col
              class="pb-6"
              cols="6">
                <div class="d-flex justify-center">
                  <attachments-dialog-trigger
                  @click="openServiceAttachmentsDialog({ preview: !canAttachFilesAsService })"
                  :attachments="attachmentsByService"
                  :editable="canAttachFilesAsService"
                  :disable-on-empty="!canAttachFilesAsService"/>
                </div>
                <p class="text-center process-repair-tab-section-subtitle font-italic">
                  {{$t("btns.byService")}}
                </p>
              </v-col>
            </v-row>

            <div
            v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE']) && isEditModeEnabled"
            style="width: 100%; height: 50px; background: #0d47a1;"
            class="d-flex justify-center align-center white--text">
              <mdi-icon
              color="white"
              src="alarm"/>
              <span class="font-weight-bold text-subtitle-1 mx-2">
                {{$t("pages.remoteSupport.youCanUpdateSessionsForTimeout", { hours: sessionEditMode.workDate.present.hours, minutes: sessionEditMode.workDate.present.minutes })}}
              </span>
              <mdi-icon
              color="white"
              src="alarm"/>
            </div>
            <v-form
            v-for="(s, i) in sessions"
            :key="i"
            v-model="s.form"
            class="px-7 pb-7 elevation-2">
              <p
              class="process-repair-tab-section-title mt-5"
              style="font-size: 20px; position: relative; top: 20px;">
                {{$t('textFields.serviceReviewSession', {"number" : i + 1})}}
              </p>
              <v-row
              justify="center">
                <v-col
                cols="6">
                  <text-field-number 
                  v-model="s.durationInHours" 
                  :label="$t('textFields.durationInHours')" 
                  class="mr-4"
                  double
                  :disabled="!unlockedSessions || $parent.isNotRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"/>
                </v-col>
                <v-col
                cols="6">
                  <text-field-date 
                  v-model="s.completionDate" 
                  :label="$t('textFields.serviceReviewSessionCompletionDate', {'value': getTimeZoneInNumber(new Date())})" 
                  :disabled="!unlockedSessions ||$parent.isNotRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"/>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <text-area 
                  v-model="s.summary" 
                  :label="$t('textFields.summary')" 
                  :disabled="!unlockedSessions || $parent.isNotRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
                  :max="2048"
                  height="auto"/>
                </v-col>
              </v-row>
              <v-row
              v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])">
                <v-col cols="4"></v-col>
                <v-col
                cols="4"
                class="d-flex justify-center">
                  <outlined-btn
                  @click="tryToSaveData(s)"
                  :label="$t('btns.save')"
                  :width="65"
                  :height="35"
                  :disabled="!unlockedSessions || !s.form"
                  :loading="isLoading"/>
                </v-col>
                <v-col
                cols="4"
                class="d-flex justify-end">
                  <delete-btn
                  @click="tryToDelete(s.id, i)"
                  :loading="isLoading"
                  :disabled="!unlockedSessions"/>
                </v-col>
              </v-row>
            </v-form>

            <v-row
            v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])">
              <v-col
              class="d-flex justify-center pt-8">
                <primary-btn
                @click="addSession"
                color="#4285f4"
                :label="$t('btns.newSession')"
                icon="plus"
                :disabled="!unlockedSessions"
                :loading="isLoading"/>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    
    <v-row
    v-if="$parent.isRepairStatus(['SERVICE_REVIEW']) && $parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE']) && !isEditModeEnabled">
      <v-col
      class="d-flex justify-center">
        <process-repair-quick-btns 
        :loading.sync="isLoading"
        :config="getQuickBtnsConfig"
        on-success-repair-status-message="dialogs.messages.success.waitingCompletion"/>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import TextArea from '@/components/text-fields/TextArea'
import TextFieldDate from '@/components/text-fields/TextFieldDate'
import TextFieldNumber from '@/components/text-fields/TextFieldNumber'
import ProcessRepairQuickBtns from '@/components/process-repair-tabs/ProcessRepairQuickBtns'
import SectionTitleWithRequestId from '@/components/common/process-tabs/shared/SectionTitleWithRequestId'
import AttachmentsDialogTrigger from '@/components/common/triggers/AttachmentsDialogTrigger'
import AttachmentDialog from '@/components/dialogs/AttachmentDialog'
import SaveableAttachmentDialog from '@/components/dialogs/SaveableAttachmentDialog'
import DateUtil from '@/utils/DateUtil'
import OutlinedBtn from '@/components/btns/OutlinedBtn'
import PrimaryBtn from '@/components/btns/PrimaryBtn'
import DeleteBtn from '@/components/btns/DeleteBtn'
import MdiIcon from '@/components/btns/MdiIcon'
import { processServiceReviewSession, deleteServiceReviewSessionById, saveAttachments } from '@/repositories/supportRepo'
import { execHttpRequestThrowable } from '@/services/http'
import { setupTimeout } from '@/utils/timeout'
import { UTCToLocal } from "@/utils/DateUtil"
//ENABLE_CHANGE_DIALOG - This method is needed for changes dialog.
// compareObjects
import { copyArray, cloneObj } from '@/utils/StringUtil'
export default {
  name: 'ServiceReviewTabRemoteSupport',
  components: {
    TextFieldDate,
    TextFieldNumber,
    TextArea,
    ProcessRepairQuickBtns,
    SectionTitleWithRequestId,
    AttachmentsDialogTrigger,
    AttachmentDialog,
    SaveableAttachmentDialog,
    OutlinedBtn,
    PrimaryBtn,
    DeleteBtn,
    MdiIcon
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    disabled: Boolean
  },
  data() {
    return {
      newData: null,
      isLoading: false,
      isDataReady: false,
      attachmentDialog: {
        model: false,
        preview: false
      },
      serviceAttachmentDialog: {
        model: false,
        preview: false
      },

      sessionEditMode: {
        workDate: null,
        intervalId: null
      }
    }
  },
  methods: {
    async tryToSaveData(sessionToBeSaved) {
      const IS_SESSION_NEW = sessionToBeSaved.id === null || sessionToBeSaved.id === undefined || sessionToBeSaved.id === -1
      const messages = {
        _200: 'dialogs.messages.success.serviceReviewDataSavedSuccessfully'
      }

      const { allowedToContinue, session } = (await execHttpRequestThrowable(
        async () => await processServiceReviewSession(this.$route.params.id, sessionToBeSaved),
        messages,
        v => this.isLoading = v)).response.data
      
      if (IS_SESSION_NEW) {
        this.remoteSupportProp.serviceReviewSessions.push(session)
        sessionToBeSaved.id = session.id
      }
      else {
        const oldSession = this.remoteSupportProp.serviceReviewSessions.filter(o => o.id === sessionToBeSaved.id)[0]
        cloneObj(session, oldSession)
      }
      
      this.newData.isAllowedToContinue = allowedToContinue
    },
    setChangedDialogItems() {
      this.tryToSaveData()  
      // NOTE: ENABLE_CHANGE_DIALOG - Enable this code, if changes dialog is needed.
      //-----------------------------------------------------------------
      // const old = this.remoteSupportProp.serviceReview
      // const _new = this.newData.serviceReview
      // const props = [
      //   {
      //     field: 'textFields.durationInHours',
      //     prop: 'durationInHours'
      //   }, {
      //     field: {
      //       value: 'textFields.completionDate',
      //       params: {
      //         'value': this.getTimeZoneInNumber(new Date())
      //       }
      //     },
      //     prop: 'completionDate'
      //   }, {
      //     field: 'textFields.summary',
      //     prop: 'summary'
      //   }
      // ]

      // this.$changesDialog.open(
      //   'dialogs.titles.changes',
      //   compareObjects(old, _new, props),
      //   this.tryToSaveData,
      //   false
      // )
      //-----------------------------------------------------------------
    },
    getTimeZoneInNumber(d) {
      return DateUtil.getTimeZoneInNumber(d)
    },
    openAttachmentsDialog(attachments, { preview }) {
      this.attachmentDialog = {
        model: true,
        attachments,
        preview
      }
    },
    openServiceAttachmentsDialog({ preview }) {
      this.serviceAttachmentDialog = {
        model: true,
        preview
      }
    },
    async tryToDelete(id, index) {
      const IS_SERVER_STORED = id !== null && id !== undefined && id !== -1
      
      // NOTE: delete on server
      if (IS_SERVER_STORED) {
        const r = await execHttpRequestThrowable(async () => await deleteServiceReviewSessionById(id), {}, v => this.isLoading = v)
        this.newData.isAllowedToContinue = r.response.data

        this.remoteSupportProp.serviceReviewSessions = [...this.remoteSupportProp.serviceReviewSessions.filter(o => o.id !== id)]
      }

      this.newData.sessions.splice(index, 1)
    },
    addSession() {
      this.newData.sessions.push({
        id: -1,
        durationInHours: null,
        completionDate: null,
        summary: null,
        form: false
      })
    },
    setupTimerForSessionEditModeIfAny() {
      if (!this.isEditModeEnabled) return

      const { data, intervalId } = setupTimeout(this.sessionEditModeEndAt)
      this.sessionEditMode.workDate = data
      this.sessionEditMode.intervalId = intervalId
      this.$watch("sessionEditMode.workDate.expired", (isExpired) => {
        if (isExpired) {
          this.remoteSupportProp.serviceReviewSessionsEditMode.enabled = false
          clearInterval(this.sessionEditMode.intervalId)
        }
      }, {
        immediate: true,
        deep: true
      })
    },
    async tryToSaveAttachmentsByService() {
      const REQ_ID = this.$route.params.id

      const r = await execHttpRequestThrowable(async () => await saveAttachments(REQ_ID, this.attachmentsByService), {}, v => this.isLoading = v)

      this.remoteSupportProp.attachmentsByService = [...r.response.data]
    }
  },
  computed: {
    remoteSupportProp: {
      get: function() {
        return this.$props.data
      },
      set: function(v) {
        this.$emit('update:data', v)
      }
    },
    sessions() {
      return this.newData.sessions
    },
    attachmentsByClient() {
      return this.remoteSupportProp.attachmentsByClient
    },
    attachmentsByService() {
      return this.remoteSupportProp.attachmentsByService
    },
    getQuickBtnsConfig() {
      return [
        {
          name: 'Continue',
          props: {
            'disabled': this.disabled || !this.newData.isAllowedToContinue
          }
        }
      ]
    },
    isEditModeEnabled() {
      return this.remoteSupportProp.serviceReviewSessionsEditMode.enabled
    },
    sessionEditModeEndAt() {
      return UTCToLocal(this.remoteSupportProp.serviceReviewSessionsEditMode.endAt)
    },
    unlockedSessions() {
      return this.$parent.isRepairStatus(["SERVICE_REVIEW", "WAITING_COMPLETION"]) || !this.disabled || this.isEditModeEnabled
    },
    canAttachFilesAsService() {
      return this.$parent.isRepairStatus(["SERVICE_REVIEW"]) && this.$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE']) && !this.disabled
    }
  },
  mounted() {
    const copied = copyArray(this.remoteSupportProp.serviceReviewSessions)
    for (const s of copied) {
      s.completionDate = s.completionDate ? s.completionDate.replaceAll(/T.*$/g, '') : ''
      s.form = false
    }
    
    this.newData = {
      isAllowedToContinue: this.remoteSupportProp.isAllowedToContinue,
      sessions: copied
    }

    this.setupTimerForSessionEditModeIfAny()
    this.isDataReady = true
  },
  beforeDestroy() {
    if (this.sessionEditMode.intervalId) clearInterval(this.sessionEditMode.intervalId)
  }
}
</script>
<style>

</style>